/**
 * 新版 UI 柱状图样式（echart.html 图表 3 — 选中高亮柱形图）
 */
const echarts = require('echarts/lib/echarts')

const BAR_COLORS = ['#1778DB', '#38CB89', '#FF8E15', '#60ADFF', '#FFB411', '#FF6B6B']
const PILL_RADIUS = [15.5, 15.5, 1, 1]
const HIGHLIGHT_RADIUS = [7.5, 7.5, 7.5, 7.5]
const HIGHLIGHT_BG_FLAG = '__barHighlightBg'

const AXIS_STYLE_X = {
    axisLine: { lineStyle: { color: '#e0e6ed' } },
    axisTick: { show: false },
    axisLabel: { color: '#b0b8c4', fontSize: 12 },
}

const AXIS_STYLE_Y = {
    splitLine: { lineStyle: { color: '#f0f2f5' } },
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: '#b0b8c4', fontSize: 12 },
}

const BAR_GRID_BASE = { left: 50, right: 20, bottom: 40, containLabel: true }

function mergeAxisStyle(axis, style) {
    if (!axis) return axis
    const merged = Object.assign({}, axis, style)
    if (style.axisLine) {
        merged.axisLine = Object.assign({}, style.axisLine, axis.axisLine || {})
    }
    if (style.axisTick) {
        merged.axisTick = Object.assign({}, style.axisTick, axis.axisTick || {})
    }
    if (style.axisLabel) {
        merged.axisLabel = Object.assign({}, style.axisLabel, axis.axisLabel || {})
    }
    if (style.splitLine) {
        merged.splitLine = Object.assign({}, style.splitLine, axis.splitLine || {})
    }
    return merged
}

function getCategories(option) {
    const xAxis = option.xAxis
    if (Array.isArray(xAxis)) {
        const visible = xAxis.find((x) => x && x.show !== false) || xAxis[0]
        return (visible && visible.data) || []
    }
    return (xAxis && xAxis.data) || []
}

function calcYMax(barSeries) {
    let max = 0
    barSeries.forEach((s) => {
        ;(s.data || []).forEach((v) => {
            const n = typeof v === 'object' && v !== null ? v.value : v
            if (typeof n === 'number' && !isNaN(n) && n > max) max = n
        })
    })
    return max > 0 ? max * 1.15 : 1
}

function formatValue(v) {
    if (v === null || v === undefined || v === '') return '-'
    if (typeof v === 'object' && v !== null && v.value !== undefined) return v.value
    return v
}

function getBarTooltip() {
    return {
        trigger: 'axis',
        axisPointer: { type: 'none' },
        backgroundColor: '#fff',
        borderColor: '#e8eef5',
        borderWidth: 1,
        textStyle: { color: '#333', fontSize: 13 },
        formatter(params) {
            const dataBars = (params || []).filter((p) => p.seriesType === 'bar' && p.seriesId !== HIGHLIGHT_BG_FLAG)
            if (dataBars.length === 0) return ''
            const title = dataBars[0].axisValueLabel || dataBars[0].name || '数据'
            let html = '<div style="font-weight:600;margin-bottom:6px">' + title + '</div>'
            dataBars.forEach((p, i) => {
                const color = BAR_COLORS[i % BAR_COLORS.length]
                const dot =
                    '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:' +
                    color +
                    ';margin-right:4px;vertical-align:middle"></span>'
                const label = p.seriesName ? p.seriesName + ': ' : ''
                html += '<div style="line-height:22px">' + dot + label + formatValue(p.value) + '</div>'
            })
            return html
        },
    }
}

const BAR_WIDTH_BASE = 12
const HIGHLIGHT_WIDTH_BASE = 56
const SINGLE_SERIES_WIDTH_MULTIPLIER = 2

function resolveCategories(categories, barSeries) {
    if (categories.length > 0) return categories.slice()
    const dataLen = barSeries.reduce((max, s) => Math.max(max, (s.data || []).length), 0)
    if (dataLen === 0) return []
    return Array.from({ length: dataLen }, (_, i) => `${i + 1}`)
}

/** 每组仅一个柱子：只有一个 bar 系列，宽度为多系列时的 2 倍 */
function calcBarLayout(barSeriesCount) {
    if (barSeriesCount <= 1) {
        return {
            barWidth: BAR_WIDTH_BASE * SINGLE_SERIES_WIDTH_MULTIPLIER,
            barCategoryGap: '50%',
            barGap: '30%',
            highlightWidth: HIGHLIGHT_WIDTH_BASE * SINGLE_SERIES_WIDTH_MULTIPLIER,
        }
    }

    return {
        barWidth: BAR_WIDTH_BASE,
        barCategoryGap: '50%',
        barGap: '30%',
        highlightWidth: HIGHLIGHT_WIDTH_BASE,
    }
}

function styleBarSeries(series, colorIndex, layout, isFirst) {
    const color = BAR_COLORS[colorIndex % BAR_COLORS.length]
    series.barWidth = layout.barWidth
    if (isFirst) {
        series.barGap = layout.barGap
        series.barCategoryGap = layout.barCategoryGap
    }
    series.z = 2
    series.itemStyle = Object.assign({}, series.itemStyle, {
        borderRadius: PILL_RADIUS,
        color,
    })
    series.emphasis = {
        itemStyle: {
            borderRadius: PILL_RADIUS,
            color,
        },
    }
    return series
}

function buildHighlightSeries(categories, yMax, layout) {
    return {
        type: 'bar',
        id: HIGHLIGHT_BG_FLAG,
        xAxisIndex: 1,
        data: categories.map(() => yMax),
        barWidth: layout.highlightWidth,
        z: 0,
        silent: true,
        animation: false,
        itemStyle: { color: 'transparent', borderRadius: HIGHLIGHT_RADIUS },
        emphasis: {
            disabled: false,
            itemStyle: { color: '#EBF2FF', borderRadius: HIGHLIGHT_RADIUS },
        },
    }
}

/**
 * 将 demo 柱状图样式合并到已有 option（保留 data、legend、title 等业务配置）
 */
export function applyBarChartTheme(option) {
    if (!option || !option.series) return option

    const barSeries = option.series.filter((s) => s && s.type === 'bar' && s.id !== HIGHLIGHT_BG_FLAG)
    if (barSeries.length === 0) return option

    const categories = resolveCategories(getCategories(option), barSeries)
    const yMax = calcYMax(barSeries)
    const barLayout = calcBarLayout(barSeries.length)

    option.axisPointer = { link: [{ xAxisIndex: 'all' }] }

    const baseTooltip = getBarTooltip()
    option.tooltip = Object.assign({}, baseTooltip, option.tooltip || {})
    if (!option.tooltip.formatter) {
        option.tooltip.axisPointer = Object.assign({}, baseTooltip.axisPointer, option.tooltip.axisPointer || {})
    }

    const top = option.title && option.title.text ? 50 : 50
    option.grid = Object.assign({}, BAR_GRID_BASE, { top }, option.grid || {})

    const visibleXAxis = mergeAxisStyle(
        Object.assign({ type: 'category', data: categories, boundaryGap: true }, option.xAxis && !Array.isArray(option.xAxis) ? option.xAxis : {}),
        AXIS_STYLE_X
    )
    const hiddenXAxis = { type: 'category', data: categories, show: false }
    option.xAxis = [visibleXAxis, hiddenXAxis]

    if (option.yAxis) {
        if (Array.isArray(option.yAxis)) {
            option.yAxis = option.yAxis.map((y) => mergeAxisStyle(y, AXIS_STYLE_Y))
        } else {
            option.yAxis = mergeAxisStyle(option.yAxis, AXIS_STYLE_Y)
        }
    }

    const otherSeries = option.series.filter((s) => !s || s.type !== 'bar' || s.id === HIGHLIGHT_BG_FLAG)
    const styledBars = barSeries.map((s, i) => styleBarSeries(Object.assign({}, s), i, barLayout, i === 0))

    option.series = [buildHighlightSeries(categories, yMax, barLayout), ...styledBars, ...otherSeries.filter((s) => s.type !== 'bar')]

    return option
}
